export function ensure_trailing_slash(url) {
    if (!url) {
        return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
}

export function format_api_errors(error_payload) {
    if (!error_payload) {
        return "Something went wrong while saving the tariff assignment.";
    }

    const normalized_payload = error_payload.errors || error_payload.detail || error_payload.error || error_payload;

    if (typeof normalized_payload === "string") {
        return normalized_payload;
    }

    if (Array.isArray(normalized_payload)) {
        return normalized_payload.join(", ");
    }

    if (typeof normalized_payload === "object") {
        return Object.entries(normalized_payload)
            .map(([field, messages]) => {
                const message_text = Array.isArray(messages) ? messages.join(", ") : messages;
                return `${field}: ${message_text}`;
            })
            .join(" | ");
    }

    return "Something went wrong while saving the tariff assignment.";
}

export function normalize_properties_response(payload) {
    if (!Array.isArray(payload)) {
        return [];
    }

    return payload
        .map((property) => ({
            id: property?.id ?? "",
            name: property?.name || "Unnamed Property",
            is_active: Boolean(property?.is_active),
            created_at: property?.created_at || "",
            updated_at: property?.updated_at || ""
        }))
        .filter((property) => property.id !== "" && property.name)
        .sort((first_property, second_property) => first_property.name.localeCompare(second_property.name));
}

export function sort_assignments_by_priority(assignments) {
    return [...assignments].sort((first_assignment, second_assignment) => {
        if (Boolean(first_assignment?.is_active) !== Boolean(second_assignment?.is_active)) {
            return first_assignment?.is_active ? -1 : 1;
        }

        const first_effective_from = new Date(first_assignment?.effective_from || 0).getTime();
        const second_effective_from = new Date(second_assignment?.effective_from || 0).getTime();

        return second_effective_from - first_effective_from;
    });
}

export function safe_parse_maybe_double_encoded_json(value, fallback_value = null) {
    if (value === null || value === undefined || value === "") {
        return fallback_value;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return fallback_value;
    }

    try {
        let parsed_value = JSON.parse(value);

        if (typeof parsed_value === "string") {
            parsed_value = JSON.parse(parsed_value);
        }

        return parsed_value;
    } catch (error) {
        return fallback_value;
    }
}

export function get_positive_integer_id(value) {
    const numeric_value = Number(value);

    return Number.isInteger(numeric_value) && numeric_value > 0 ? String(numeric_value) : "";
}

export function add_days_to_iso_date(iso_date, days_to_add) {
    const base_date = new Date(`${iso_date}T00:00:00`);

    if (Number.isNaN(base_date.getTime())) {
        return new Date().toISOString().slice(0, 10);
    }

    base_date.setDate(base_date.getDate() + days_to_add);
    return base_date.toISOString().slice(0, 10);
}

export function normalize_ajax_payload(payload, fallback_text = "") {
    if (payload === null || payload === undefined || payload === "") {
        return fallback_text
            ? safe_parse_maybe_double_encoded_json(fallback_text, null)
            : null;
    }

    if (typeof payload === "string") {
        return safe_parse_maybe_double_encoded_json(payload, payload);
    }

    return payload;
}

export function extract_assignment_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.results)) {
        return payload.results;
    }

    return [];
}

export function get_assignment_property_id(assignment) {
    return Number(
        assignment?.property?.id
        ?? assignment?.property_id
        ?? assignment?.property
    );
}

export function get_assignment_tariff_id(assignment) {
    return Number(
        assignment?.tariff?.id
        ?? assignment?.tariff_id
        ?? assignment?.tariff
    );
}

export function normalize_assignment_effective_from(assignment) {
    return String(assignment?.effective_from || "").slice(0, 10);
}

export function build_url_with_query(base_url, query_params = {}) {
    const query_string = new URLSearchParams(query_params).toString();

    return query_string ? `${base_url}?${query_string}` : base_url;
}

export function merge_assignment_records(primary_payload, history_payload) {
    return [
        ...extract_assignment_records(primary_payload),
        ...extract_assignment_records(history_payload)
    ].reduce((records, assignment) => {
        const assignment_key = String(assignment?.id || "");

        if (!assignment_key || records.some((record) => String(record?.id || "") === assignment_key)) {
            return records;
        }

        return [...records, assignment];
    }, []);
}

function handle_ajax_success(resolve, response_data, jqXHR) {
    resolve(normalize_ajax_payload(response_data, jqXHR?.responseText || ""));
}

function handle_ajax_error(reject, jqXHR) {
    const error_payload = normalize_ajax_payload(
        jqXHR?.responseJSON ?? jqXHR?.responseText,
        jqXHR?.responseText || ""
    );

    reject(new Error(format_api_errors(error_payload)));
}

function build_ajax_options(url, options, resolve, reject) {
    return ({
        url,
        method: options.method || "GET",
        headers: options.headers || {},
        contentType: options.contentType || options.headers?.["Content-Type"] || undefined,
        data: options.body,
        processData: options.processData ?? false,
        success(response_data, _text_status, jqXHR) {
            handle_ajax_success(resolve, response_data, jqXHR);
        },
        error(jqXHR) {
            handle_ajax_error(reject, jqXHR);
        }
    });
}

export function ajax_json_request(url, options = {}) {
    return new Promise((resolve, reject) => {
        const jquery_instance = window.jQuery || window.$;

        if (!jquery_instance?.ajax) {
            reject(new Error("jQuery AJAX is not available."));
            return;
        }

        jquery_instance.ajax(build_ajax_options(url, options, resolve, reject));
    });
}

export function extract_rate_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.results)) {
        return payload.results;
    }

    return [];
}

export function extract_tariff_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.results)) {
        return payload.results;
    }

    return [];
}

function first_present_value(values, fallback_value = "") {
    const value = values.find((candidate) => candidate !== null && candidate !== undefined && candidate !== "");

    return value ?? fallback_value;
}

function first_defined_value(values, fallback_value = null) {
    const value = values.find((candidate) => candidate !== null && candidate !== undefined);

    return value ?? fallback_value;
}

function get_rate_metadata(rate) {
    const metadata_source = first_present_value([
        rate?.metadata_json,
        rate?.metadata,
        rate?.extra_data,
        rate?.matrix_metadata,
        rate?.details
    ], null);

    return safe_parse_maybe_double_encoded_json(metadata_source, {}) || {};
}

function normalize_array_rate_record(rate) {
    const metadata = safe_parse_maybe_double_encoded_json(rate[6], {}) || {};

    return ({
        id: rate[0],
        name: first_present_value([rate[1]], "Unnamed tariff rate"),
        code: first_present_value([rate[3]]),
        matrix_code: first_present_value([metadata["Matrix Code"], rate[3]]),
        effective_from: first_present_value([rate[7]]),
        effective_to: first_present_value([rate[12]]),
        tariff_book_id: first_present_value([rate[11]], null),
        tariff_id: first_defined_value([rate[13]], null),
        rates: safe_parse_maybe_double_encoded_json(rate[5], {}) || {}
    });
}

function normalize_object_rate_record(rate) {
    const metadata = get_rate_metadata(rate);

    return ({
        id: rate?.id,
        name: first_present_value([rate?.name, rate?.tariff_name], "Unnamed tariff rate"),
        code: first_present_value([rate?.code, rate?.matrix_code, metadata["Matrix Code"]]),
        matrix_code: first_present_value([rate?.matrix_code, metadata["Matrix Code"], rate?.code]),
        effective_from: first_present_value([rate?.effective_from, rate?.start_date]),
        effective_to: first_present_value([rate?.effective_to, rate?.end_date]),
        tariff_book_id: first_defined_value([rate?.tariff_book_id, rate?.tariff_book?.id, rate?.tariff_book]),
        tariff_id: first_defined_value([rate?.tariff_id, rate?.tariff?.id, rate?.tariff]),
        rates: safe_parse_maybe_double_encoded_json(
            rate?.rates_json ||
            rate?.charge_components ||
            rate?.rates ||
            rate?.rate_values ||
            rate?.rate_data ||
            rate?.charges ||
            rate?.tariff_rates,
            {}
        ) || {}
    });
}

export function normalize_list_rate_record(rate) {
    if (Array.isArray(rate)) {
        return normalize_array_rate_record(rate);
    }

    return normalize_object_rate_record(rate);
}

export function normalize_tariff_record(tariff) {
    const tariff_book_relation = tariff?.tariff_book || {};
    const municipality_relation = tariff?.municipality || {};

    return ({
        id: tariff?.id ?? "",
        tariff_book_id: first_defined_value([tariff?.tariff_book_id, tariff_book_relation?.id, tariff_book_relation]),
        tariff_book_name: first_present_value([tariff_book_relation?.name, tariff?.tariff_book_name], "Unknown Tariff Book"),
        name: first_present_value([tariff?.tariff_name, tariff?.name], "Unnamed tariff"),
        full_name: first_present_value([tariff?.full_name, tariff?.description]),
        code: first_present_value([tariff?.code, tariff?.tariff_code], "-"),
        matrix_code: first_present_value([tariff?.matrix_code, tariff?.tariff_matrix_code], "-"),
        tariff_type: first_present_value([tariff?.tariff_type, tariff?.type], "-"),
        tariff_category: first_present_value([tariff?.category, tariff?.tariff_category, tariff_book_relation?.tariff_category], "-"),
        period: first_present_value([tariff_book_relation?.period, tariff?.period], "-"),
        municipality_id: first_defined_value([tariff?.municipality_id, municipality_relation?.id, tariff?.municipality]),
        municipality_name: first_present_value([municipality_relation?.name, tariff?.municipality_name], "-"),
        raw: tariff
    });
}

export function build_tariff_lookup(tariff_records) {
    return tariff_records.reduce((lookup, tariff_record) => {
        const normalized_tariff = normalize_tariff_record(tariff_record);
        const tariff_book_id = Number(normalized_tariff.tariff_book_id);

        if (!Number.isFinite(tariff_book_id)) {
            return lookup;
        }

        const tariff_book_key = String(parseInt(tariff_book_id, 10));

        if (!lookup[String(tariff_book_key)]) {
            lookup[String(tariff_book_key)] = [];
        }

        lookup[String(tariff_book_key)].push(normalized_tariff);
        return lookup;
    }, {});
}

export function build_tariff_rate_lookup(rate_records) {
    return rate_records.reduce((lookup, rate_record) => {
        const normalized_rate = normalize_list_rate_record(rate_record);
        const tariff_book_id = Number(normalized_rate.tariff_book_id);
        const tariff_book_key = String(parseInt(tariff_book_id, 10));

        if (!Number.isFinite(tariff_book_id)) {
            return lookup;
        }

        if (!lookup[String(tariff_book_key)]) {
            lookup[String(tariff_book_key)] = [];
        }

        const has_existing_rate = lookup[String(tariff_book_key)].some((rate) => rate.name === normalized_rate.name);

        if (!has_existing_rate) {
            lookup[String(tariff_book_key)].push(normalized_rate);
        }

        return lookup;
    }, {});
}

export function format_settings_date(date_string) {
    if (!date_string) {
        return "Open-ended";
    }

    const date = new Date(date_string);

    if (Number.isNaN(date.getTime())) {
        return date_string;
    }

    return new Intl.DateTimeFormat("en-ZA", {
        year: "numeric",
        month: "short",
        day: "2-digit"
    }).format(date);
}

function get_flattened_tariffs(tariff_lookup = {}) {
    return Object.values(tariff_lookup).flat();
}

function get_tariff_book_from_tariff_lookup(row, tariff_lookup = {}) {
    const assigned_tariff_id = get_assignment_tariff_id(row);

    if (!Number.isFinite(assigned_tariff_id)) {
        return {};
    }

    const matching_tariff = get_flattened_tariffs(tariff_lookup).find((tariff) => {
        return Number(tariff.id) === assigned_tariff_id;
    });

    if (!matching_tariff?.tariff_book_id) {
        return {};
    }

    return ({
        id: matching_tariff.tariff_book_id,
        name: matching_tariff.tariff_book_name,
        tariff_category: matching_tariff.tariff_category,
        period: matching_tariff.period,
        authority: matching_tariff.authority,
        local_authority: matching_tariff.local_authority
    });
}

function normalize_tariff_book_relation(tariff_book, fallback_tariff_book = {}) {
    if (tariff_book && typeof tariff_book === "object") {
        return tariff_book;
    }

    if (tariff_book !== null && tariff_book !== undefined && tariff_book !== "") {
        return ({
            ...fallback_tariff_book,
            id: tariff_book
        });
    }

    return fallback_tariff_book || {};
}

function get_assignment_tariff_book(row, tariff_lookup = {}) {
    const fallback_tariff_book = get_tariff_book_from_tariff_lookup(row, tariff_lookup);

    return normalize_tariff_book_relation(
        row.tariff_book || row.tariff?.tariff_book || row.tariff_book_id || row.tariff?.tariff_book_id,
        fallback_tariff_book
    );
}

function get_tariff_book_key(tariff_book) {
    const tariff_book_id = Number(tariff_book.id);

    return Number.isFinite(tariff_book_id) ? String(parseInt(tariff_book_id, 10)) : "";
}

function get_related_tariffs(tariff_lookup, tariff_book) {
    const tariff_book_key = get_tariff_book_key(tariff_book);

    return tariff_book_key ? (tariff_lookup[String(tariff_book_key)] || []) : [];
}

function get_primary_tariff(related_tariffs, assigned_tariff) {
    return related_tariffs.find((tariff) => {
        return Number(tariff.id) === Number(assigned_tariff?.id);
    }) || related_tariffs[0] || {};
}

function get_assignment_authority(tariff_book) {
    return first_present_value([
        tariff_book.authority,
        tariff_book.local_authority,
        tariff_book.notes,
        tariff_book.tariff_category
    ], "-");
}

function get_assignment_tariff_fields(assigned_tariff, primary_tariff, tariff_book) {
    return ({
        tariff_id: first_present_value([assigned_tariff?.id, primary_tariff.id]),
        tariff_name: first_present_value([assigned_tariff.tariff_name, assigned_tariff.name, primary_tariff.name], "No tariff linked"),
        tariff_code: first_present_value([assigned_tariff.code, primary_tariff.code], "-"),
        matrix_code: first_present_value([assigned_tariff.matrix_code, primary_tariff.matrix_code], "-"),
        tariff_category: first_present_value([
            assigned_tariff.category,
            assigned_tariff.tariff_category,
            primary_tariff.tariff_category,
            tariff_book.tariff_category
        ], "-")
    });
}

export function normalize_assignment_row(row, tariff_lookup = {}) {
    const tariff_book = get_assignment_tariff_book(row, tariff_lookup);
    const assigned_tariff = row.tariff || {};
    const related_tariffs = get_related_tariffs(tariff_lookup, tariff_book);
    const primary_tariff = get_primary_tariff(related_tariffs, assigned_tariff);
    const tariff_fields = get_assignment_tariff_fields(assigned_tariff, primary_tariff, tariff_book);

    return ({
        id: row.id,
        tariff_book_id: tariff_book.id || null,
        property_name: row.property?.name || "Unknown Property",
        property_subtitle: `Property ID ${get_assignment_property_id(row) || "-"}`,
        ...tariff_fields,
        tariff_name_list: related_tariffs.map((tariff) => tariff.name),
        tariff_book_name: tariff_book.name || "Unknown Tariff Book",
        authority: get_assignment_authority(tariff_book),
        period: tariff_book.period || "-",
        effective_from: row.effective_from || "",
        effective_to: row.effective_to || "",
        is_active: Boolean(row.is_active),
        raw: row
    });
}

function get_latest_tariff_rate(tariff_id, rate_records = []) {
    const normalized_tariff_id = Number(tariff_id);

    if (!Number.isFinite(normalized_tariff_id)) {
        return null;
    }

    return rate_records
        .filter((rate) => Number(rate.tariff_id) === normalized_tariff_id)
        .sort((first_rate, second_rate) => {
            const first_effective_from = new Date(first_rate?.effective_from || 0).getTime();
            const second_effective_from = new Date(second_rate?.effective_from || 0).getTime();

            return second_effective_from - first_effective_from;
        })[0] || null;
}

function add_latest_rate_dates(row, rate_records = []) {
    const latest_rate = get_latest_tariff_rate(row.tariff_id, rate_records);

    return ({
        ...row,
        rate_effective_from: latest_rate?.effective_from || "",
        rate_effective_to: latest_rate?.effective_to || ""
    });
}

export function build_table_rows(properties, assignments, tariff_lookup = {}, rate_records = []) {
    const assignments_by_property = assignments.reduce((result, assignment) => {
        const property_id = get_assignment_property_id(assignment);
        const property_key = String(parseInt(property_id, 10));

        if (!property_id) {
            return result;
        }

        if (!result[String(property_key)]) {
            result[String(property_key)] = [];
        }

        result[String(property_key)].push(assignment);
        return result;
    }, {});

    return properties.map((property) => {
        const property_key = String(parseInt(property.id, 10));
        const property_assignments = sort_assignments_by_priority(assignments_by_property[String(property_key)] || []);
        const selected_assignment = property_assignments.find((assignment) => Boolean(assignment?.is_active)) || null;

        if (!selected_assignment) {
            return ({
                id: `property-${property.id}`,
                property_id: property.id,
                property_name: property.name,
                property_subtitle: "No tariff assigned",
                tariff_id: "",
                tariff_name: "No tariff assigned",
                tariff_name_list: [],
                tariff_code: "-",
                tariff_book_name: "No tariff book assigned",
                authority: "-",
                tariff_category: "-",
                period: "-",
                effective_from: "",
                effective_to: "",
                rate_effective_from: "",
                rate_effective_to: "",
                is_active: property.is_active,
                has_assignment: false,
                tariff_book_id: null,
                raw: null
            });
        }

        const normalized_assignment = normalize_assignment_row(selected_assignment, tariff_lookup);

        return add_latest_rate_dates({
            ...normalized_assignment,
            property_id: property.id,
            property_name: property.name,
            has_assignment: true
        }, rate_records);
    });
}
